import React from 'react';
import styled from 'styled-components';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LaunchIcon from '@mui/icons-material/Launch';
import { publications, achievements } from '../../data/constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 40px 0px 80px 0px;
  @media (max-width: 960px) {
    padding: 20px 0px 40px 0px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  padding: 20px 16px;
  gap: 20px;
`;

const Title = styled.h2`
  font-size: 42px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 32px;
  }
`;

const Desc = styled.p`
  font-size: 18px;
  text-align: center;
  max-width: 700px;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 24px;
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 16px;
  }
`;

const SectionHeader = styled.h3`
  font-size: 26px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  margin-top: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CardGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-bottom: 36px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const PubCard = styled.div`
  background: ${({ theme }) => theme.card || theme.colors.background2};
  border: 1px solid #306EE8;
  box-shadow: 0 4px 24px rgba(23, 92, 230, 0.15);
  border-radius: 14px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  transition: all 0.3s ease-in-out;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(133, 76, 230, 0.25);
    border-color: ${({ theme }) => theme.primary};
  }
  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const CardTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.4;
  margin: 0;
`;

const Badge = styled.span`
  align-self: flex-start;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.primary + '1a'};
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.primary + '40'};
`;

const CardDesc = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary + 'cc'};
  line-height: 1.5;
  margin: 0;
`;

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  text-decoration: none;
  align-self: flex-start;
  transition: all 0.2s ease;
  padding: 6px 0;
  &:hover {
    color: #915EFF;
    transform: translateX(3px);
  }
`;

const AchCard = styled.div`
  background: ${({ theme }) => theme.card || theme.colors.background2};
  border: 1px solid rgba(133, 76, 230, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 24px rgba(23, 92, 230, 0.2);
    border-color: ${({ theme }) => theme.primary};
  }
`;

const Publications = () => {
  return (
    <Container id="publications">
      <Wrapper>
        <Title>Publications & Achievements</Title>
        <Desc>
          Peer-reviewed IEEE research contributions and notable competitive milestones.
        </Desc>

        {/* IEEE Publications Section */}
        <div style={{ width: '100%' }}>
          <SectionHeader>
            <MenuBookIcon /> IEEE Publications
          </SectionHeader>
          <CardGrid>
            {publications.map((pub) => (
              <PubCard key={pub.id}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Badge>{pub.conference}</Badge>
                  <CardTitle>{pub.title}</CardTitle>
                  <CardDesc>{pub.desc}</CardDesc>
                </div>
                <ActionButton
                  href={pub.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on IEEE Xplore <LaunchIcon fontSize="small" />
                </ActionButton>
              </PubCard>
            ))}
          </CardGrid>
        </div>

        {/* Achievements Section */}
        <div style={{ width: '100%' }}>
          <SectionHeader>
            <EmojiEventsIcon /> Honors & Leadership
          </SectionHeader>
          <CardGrid>
            {achievements.map((ach) => (
              <AchCard key={ach.id}>
                <Badge>{ach.detail}</Badge>
                <CardTitle>{ach.title}</CardTitle>
                <CardDesc>{ach.desc}</CardDesc>
              </AchCard>
            ))}
          </CardGrid>
        </div>
      </Wrapper>
    </Container>
  );
};

export default Publications;

